import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  CouponApi,
  CouponRuleConfig,
  CreateUpdateCouponRuleConfigRequest,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import { formSchema, CouponFormValues } from "./types";
import { formatDateForAPI } from "./utils";

interface EditCouponFormProps {
  coupon: CouponRuleConfig;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function EditCouponForm({
  coupon,
  onSuccess,
  onCancel,
}: EditCouponFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issuanceStartDateOpen, setIssuanceStartDateOpen] = useState(false);
  const [issuanceEndDateOpen, setIssuanceEndDateOpen] = useState(false);
  const [expiryDateOpen, setExpiryDateOpen] = useState(false);

  const form = useForm<CouponFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ruleName: coupon.ruleName || "",
      invoiceValueFrom: coupon.invoiceValueFrom?.toString() || "",
      invoiceValueTo: coupon.invoiceValueTo?.toString() || "",
      couponType: coupon.couponType === 1 ? "flat" : "percentage",
      couponValue: coupon.couponValue?.toString() || "",
      issuanceStartDate: coupon.issuanceFrom
        ? new Date(coupon.issuanceFrom)
        : undefined,
      issuanceEndDate: coupon.issuanceTo
        ? new Date(coupon.issuanceTo)
        : undefined,
      expiryDate: coupon.expiry ? new Date(coupon.expiry) : undefined,
      usageLimit: coupon.ruleType === 1 ? "single" : "multiple",
      redeemAmountFrom:
        coupon.redeemAmountFrom?.toString() ||
        coupon.RedeemAmountFrom?.toString() ||
        "",
      redeemAmountTo:
        coupon.redeemAmountTo?.toString() ||
        coupon.RedeemAmountTo?.toString() ||
        "",
    },
  });

  const onSubmit = async (data: CouponFormValues) => {
    setIsSubmitting(true);
    try {
      const usageLimitValue = data.usageLimit === "single" ? 1 : 2;
      const payload: CreateUpdateCouponRuleConfigRequest = {
        p_id: coupon.id,
        p_ruleName: data.ruleName,
        p_invoiceValueFrom: Number(data.invoiceValueFrom),
        p_invoiceValueTo: Number(data.invoiceValueTo),
        p_couponType: data.couponType === "flat" ? 1 : 2,
        p_couponValue: Number(data.couponValue),
        p_issuanceFrom: formatDateForAPI(data.issuanceStartDate),
        p_issuanceTo: formatDateForAPI(data.issuanceEndDate),
        p_Expiry: formatDateForAPI(data.expiryDate),
        p_ruleType: usageLimitValue,
        p_createdBy: 0,
        p_updatedBy: 1,
        p_UsageLimit: usageLimitValue,
        ...(data.redeemAmountFrom &&
          data.redeemAmountFrom.trim() !== "" && {
            p_RedeemAmountFrom: Number(data.redeemAmountFrom),
          }),
        ...(data.redeemAmountTo &&
          data.redeemAmountTo.trim() !== "" && {
            p_RedeemAmountTo: Number(data.redeemAmountTo),
          }),
      };

      const response = await CouponApi.createUpdateCouponRuleConfig(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: response.message || "Coupon rule updated successfully",
        });
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Error updating coupon:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to update coupon rule",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="ruleName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rule Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter rule name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <FormLabel className="font-bold">Earn Criteria *</FormLabel>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="invoiceValueFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Value From *</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Min value" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="invoiceValueTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Value To *</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Max value" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="couponType"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Coupon Type *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-row space-x-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="flat" id="edit-flat" />
                        <label
                          htmlFor="edit-flat"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Flat Discount
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="percentage"
                          id="edit-percentage"
                        />
                        <label
                          htmlFor="edit-percentage"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Percentage
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="usageLimit"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Usage Limit *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-row space-x-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="single" id="edit-single" />
                        <label
                          htmlFor="edit-single"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Single Use
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="multiple" id="edit-multiple" />
                        <label
                          htmlFor="edit-multiple"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Multiple Use
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="couponValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Coupon Value *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Enter coupon value"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Prevent negative values
                      if (
                        value === "" ||
                        (!isNaN(Number(value)) && Number(value) >= 0)
                      ) {
                        field.onChange(value);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <FormLabel>Issuance *</FormLabel>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="issuanceStartDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Start Date *</FormLabel>
                    <Popover
                      open={issuanceStartDateOpen}
                      onOpenChange={setIssuanceStartDateOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            if (date) {
                              setIssuanceStartDateOpen(false);
                            }
                          }}
                          disabled={(date) =>
                            date < new Date(new Date().setHours(0, 0, 0, 0))
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="issuanceEndDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>End Date *</FormLabel>
                    <Popover
                      open={issuanceEndDateOpen}
                      onOpenChange={setIssuanceEndDateOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            if (date) {
                              setIssuanceEndDateOpen(false);
                            }
                          }}
                          disabled={(date) => {
                            const startDate = form.getValues("issuanceStartDate");
                            const today = new Date(new Date().setHours(0, 0, 0, 0));
                            return (
                              date < today ||
                              (startDate && date < startDate)
                            );
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="space-y-2">
            <FormLabel className="font-bold">Redeem Criteria</FormLabel>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="redeemAmountFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Value From</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Min value" {...field} />
                    </FormControl>
                    <FormDescription>
                      Minimum invoice amount for redemption
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="redeemAmountTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Value To</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Max value" {...field} />
                    </FormControl>
                    <FormDescription>
                      Maximum invoice amount for redemption
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <FormField
            control={form.control}
            name="expiryDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Expiry Date *</FormLabel>
                <Popover open={expiryDateOpen} onOpenChange={setExpiryDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        if (date) {
                          setExpiryDateOpen(false);
                        }
                      }}
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
